const isPivotHigh = (candles, i, left, right) => {
    const h = Number(candles[i].high);
    if (!(h > 0))
        return false;
    for (let j = i - left; j <= i + right; j++) {
        if (j === i || j < 0 || j >= candles.length)
            continue;
        if (Number(candles[j].high) >= h)
            return false;
    }
    return true;
};
const isPivotLow = (candles, i, left, right) => {
    const l = Number(candles[i].low);
    if (!(l > 0))
        return false;
    for (let j = i - left; j <= i + right; j++) {
        if (j === i || j < 0 || j >= candles.length)
            continue;
        if (Number(candles[j].low) <= l)
            return false;
    }
    return true;
};
const overlaps = (a, b) => {
    const midA = (a.priceLow + a.priceHigh) / 2;
    const midB = (b.priceLow + b.priceHigh) / 2;
    const mid = Math.max(midA, midB, 1e-9);
    // Near-duplicate bands (common when several BOS share a neighbourhood).
    if (Math.abs(midA - midB) / mid < 0.01)
        return true;
    const lo = Math.max(a.priceLow, b.priceLow);
    const hi = Math.min(a.priceHigh, b.priceHigh);
    if (!(hi > lo)) {
        // Touching / tiny gap still reads as one mess on the chart.
        const gap = Math.max(a.priceLow, b.priceLow) - Math.min(a.priceHigh, b.priceHigh);
        return gap >= 0 && gap / mid < 0.004;
    }
    const ah = Math.max(1e-12, a.priceHigh - a.priceLow);
    const bh = Math.max(1e-12, b.priceHigh - b.priceLow);
    return (hi - lo) >= Math.min(ah, bh) * 0.22;
};
/**
 * Raw swing→BOS detection (live + mitigated). Callers filter via
 * `orderBlockHints` / `orderBlockBreakers`.
 */
const orderBlockHintsRaw = (candles, maxScanBlocks, sensitivity = 50) => {
    if (candles.length < 50)
        return [];
    const sens = Math.max(0, Math.min(100, Number(sensitivity) || 50)) / 100;
    // Pivot wings: strict → longer swings (5…10).
    const wings = Math.round(5 + (1 - sens) * 5);
    const want = Math.max(1, Math.min(24, Math.floor(maxScanBlocks) || 8));
    const scanFrom = Math.max(wings + 2, candles.length - Math.round(120 + sens * 80));
    const avgVol = (() => {
        let s = 0;
        let n = 0;
        for (let i = Math.max(0, candles.length - 80); i < candles.length; i++) {
            const v = Number(candles[i].volume) || 0;
            if (v > 0) {
                s += v;
                n += 1;
            }
        }
        return n > 0 ? s / n : 0;
    })();
    const swingHighs = [];
    const swingLows = [];
    for (let i = wings; i < candles.length - wings; i++) {
        if (isPivotHigh(candles, i, wings, wings)) {
            swingHighs.push({ i, price: Number(candles[i].high) });
        }
        if (isPivotLow(candles, i, wings, wings)) {
            swingLows.push({ i, price: Number(candles[i].low) });
        }
    }
    const raw = [];
    const usedSwing = new Set();
    const pushOb = (side, obIdx, breakIdx, swingKey) => {
        if (usedSwing.has(swingKey))
            return;
        usedSwing.add(swingKey);
        const ob = candles[obIdx];
        const bodyLo = Math.min(Number(ob.open), Number(ob.close));
        const bodyHi = Math.max(Number(ob.open), Number(ob.close));
        const bodyH = Math.max(1e-12, bodyHi - bodyLo);
        const pad = bodyH * 0.06;
        let priceLow = bodyLo - pad;
        let priceHigh = bodyHi + pad;
        const mid0 = (priceLow + priceHigh) / 2 || Number(ob.close) || 1;
        // Cap absurd bodies / gaps — keep a readable band, not a billboard.
        const maxH = mid0 * (0.005 + sens * 0.005); // ~0.5–1.0%
        if (priceHigh - priceLow > maxH) {
            priceLow = mid0 - maxH / 2;
            priceHigh = mid0 + maxH / 2;
        }
        const minH = mid0 * 0.0008;
        if (priceHigh - priceLow < minH) {
            priceLow = mid0 - minH / 2;
            priceHigh = mid0 + minH / 2;
        }
        const breakBar = candles[breakIdx];
        const impulseBody = Math.abs(Number(breakBar.close) - Number(breakBar.open));
        const impulsePct = (impulseBody / mid0) * 100;
        const candleVol = Number(ob.volume) || 0;
        let intervalVol = 0;
        for (let k = obIdx; k <= breakIdx; k++)
            intervalVol += Number(candles[k].volume) || 0;
        let strength = Math.min(100, Math.round(40
            + Math.min(35, impulsePct * 14)
            + (avgVol > 0 ? Math.min(25, (intervalVol / Math.max(avgVol, 1e-9)) * 4) : 0)));
        let status = 'fresh';
        let mitigatedAt = -1;
        for (let k = breakIdx + 1; k < candles.length; k++) {
            const x = candles[k];
            const touched = x.low <= priceHigh && x.high >= priceLow;
            if (!touched)
                continue;
            status = 'tested';
            // Mitigation = close through the far edge (LuxAlgo "Close" method).
            if (side === 'bull' && x.close < priceLow) {
                status = 'mitigated';
                mitigatedAt = k;
                break;
            }
            if (side === 'bear' && x.close > priceHigh) {
                status = 'mitigated';
                mitigatedAt = k;
                break;
            }
        }
        let endIdx = candles.length - 1;
        if (status === 'mitigated') {
            strength = Math.max(10, strength - 40);
            if (mitigatedAt >= 0)
                endIdx = mitigatedAt;
        }
        else if (status === 'tested') {
            strength = Math.max(22, strength - 6);
        }
        // Interval buy/sell (close-in-range × bar volume) — LuxAlgo internal activity.
        let buyAcc = 0;
        let sellAcc = 0;
        for (let k = obIdx; k <= breakIdx; k++) {
            const bar = candles[k];
            const v = Number(bar.volume) || 0;
            if (!(v > 0))
                continue;
            const rng = Math.max(1e-12, Number(bar.high) - Number(bar.low));
            const pos = Math.max(0, Math.min(1, (Number(bar.close) - Number(bar.low)) / rng));
            buyAcc += v * pos;
            sellAcc += v * (1 - pos);
        }
        const actSum = buyAcc + sellAcc;
        const buyVolPct = actSum > 0 ? Math.max(0, Math.min(100, Math.round((buyAcc / actSum) * 100))) : 50;
        const sellVolPct = 100 - buyVolPct;
        raw.push({
            tFrom: ob.time,
            tTo: candles[endIdx].time,
            priceLow,
            priceHigh,
            side,
            candleVol,
            intervalVol,
            impulsePct,
            strength,
            status,
            midPrice: (bodyLo + bodyHi) / 2,
            heightPct: ((priceHigh - priceLow) / mid0) * 100,
            buyVolPct,
            sellVolPct,
        });
    };
    // Bullish BOS: close above a prior swing high → last down-close before the break.
    for (let i = scanFrom; i < candles.length; i++) {
        const c = candles[i];
        for (let s = swingHighs.length - 1; s >= 0; s--) {
            const sw = swingHighs[s];
            if (sw.i >= i - 1)
                continue;
            if (i - sw.i > Math.round(40 + sens * 40))
                continue;
            if (!(c.close > sw.price))
                continue;
            // First close that breaks this swing.
            const prev = candles[i - 1];
            if (prev && prev.close > sw.price)
                continue;
            let obIdx = -1;
            for (let j = i - 1; j >= sw.i; j--) {
                const b = candles[j];
                if (b.close < b.open) {
                    obIdx = j;
                    break;
                }
            }
            if (obIdx < 0)
                continue;
            pushOb('bull', obIdx, i, `h:${sw.i}`);
            break;
        }
    }
    // Bearish BOS: close below a prior swing low → last up-close before the break.
    for (let i = scanFrom; i < candles.length; i++) {
        const c = candles[i];
        for (let s = swingLows.length - 1; s >= 0; s--) {
            const sw = swingLows[s];
            if (sw.i >= i - 1)
                continue;
            if (i - sw.i > Math.round(40 + sens * 40))
                continue;
            if (!(c.close < sw.price))
                continue;
            const prev = candles[i - 1];
            if (prev && prev.close < sw.price)
                continue;
            let obIdx = -1;
            for (let j = i - 1; j >= sw.i; j--) {
                const b = candles[j];
                if (b.close > b.open) {
                    obIdx = j;
                    break;
                }
            }
            if (obIdx < 0)
                continue;
            pushOb('bear', obIdx, i, `l:${sw.i}`);
            break;
        }
    }
    // Cap raw noise before callers split live vs breakers.
    return raw
        .sort((a, b) => (b.strength || 0) - (a.strength || 0) || b.tFrom - a.tFrom)
        .slice(0, want);
};
/**
 * LuxAlgo / SMC volumetric order blocks (live only).
 * Breakers → `orderBlockBreakers`. `sensitivity` 0–100: lower = fewer / stronger.
 */
export const orderBlockHints = (candles, maxBlocks = 4, sensitivity = 50, book, bookMode = 'annotate') => {
    const want = Math.max(1, Math.min(8, Math.floor(maxBlocks) || 4));
    const live = orderBlockHintsRaw(candles, Math.max(12, want * 3), sensitivity)
        .filter((z) => z.status !== 'mitigated');
    return finalizeOrderBlockList(live, want, book, bookMode);
};
/**
 * Mitigated order blocks as LuxAlgo-style breakers (polarity flipped):
 * broken demand → resistance, broken supply → support.
 */
export const orderBlockBreakers = (candles, maxBlocks = 4, sensitivity = 50, book, bookMode = 'annotate') => {
    const want = Math.max(1, Math.min(8, Math.floor(maxBlocks) || 4));
    const broken = orderBlockHintsRaw(candles, Math.max(12, want * 4), sensitivity)
        .filter((z) => z.status === 'mitigated')
        .map((z) => ({
        ...z,
        side: (z.side === 'bull' ? 'bear' : 'bull'),
        strength: Math.max(10, Math.min(55, (z.strength || 40) - 5)),
    }));
    return finalizeOrderBlockList(broken, want, book, bookMode);
};
const finalizeOrderBlockList = (ranked, want, book, bookMode = 'annotate') => {
    const filtered = [];
    for (const z of ranked) {
        if (filtered.length >= want)
            break;
        const rival = filtered.findIndex((f) => overlaps(f, z));
        if (rival >= 0) {
            const cur = filtered[rival];
            if ((z.strength || 0) > (cur.strength || 0) + 2)
                filtered[rival] = z;
            continue;
        }
        filtered.push(z);
    }
    filtered.sort((a, b) => a.tFrom - b.tFrom);
    const volSum = filtered.reduce((s, z) => s + (Number(z.intervalVol) || Number(z.candleVol) || 0), 0);
    for (const z of filtered) {
        const v = Number(z.intervalVol) || Number(z.candleVol) || 0;
        z.volSharePct = volSum > 0 ? Math.round((v / volSum) * 100) : 0;
    }
    return enrichOrderBlocksWithBook(filtered, book, bookMode);
};
/**
 * Live L2 annotate / widen. Always recount qty inside the zone from the real book.
 * `annotate` — keep candle-body band (metrics only).
 * `widen` — snap edges to significant walls near the zone.
 */
export const enrichOrderBlocksWithBook = (zones, book, mode = 'annotate') => {
    if (!zones.length)
        return zones;
    const widen = mode === 'widen';
    const bids = (book?.bids || []).filter((r) => Number(r[0]) > 0 && Number(r[1]) > 0);
    const asks = (book?.asks || []).filter((r) => Number(r[0]) > 0 && Number(r[1]) > 0);
    const hasBook = bids.length + asks.length > 0;
    return zones.map((z) => {
        const mid = (z.priceLow + z.priceHigh) / 2 || 1;
        const coreLo = z.priceLow;
        const coreHi = z.priceHigh;
        const band = mid * (widen ? 0.018 : 0.0015);
        let lo = coreLo;
        let hi = coreHi;
        let bidQty = 0;
        let askQty = 0;
        let levels = 0;
        const inBand = (px) => px >= coreLo - band && px <= coreHi + band;
        for (const [px, qty] of bids) {
            if (!inBand(px))
                continue;
            bidQty += qty;
            levels += 1;
        }
        for (const [px, qty] of asks) {
            if (!inBand(px))
                continue;
            askQty += qty;
            levels += 1;
        }
        if (widen && hasBook) {
            const maxH = mid * 0.022;
            const sideBook = z.side === 'bull' ? bids : asks;
            const walls = sideBook
                .filter(([px]) => px >= coreLo - band && px <= coreHi + band)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5);
            if (walls.length) {
                let wallLo = Math.min(...walls.map(([px]) => px));
                let wallHi = Math.max(...walls.map(([px]) => px));
                wallLo = Math.min(wallLo, coreLo);
                wallHi = Math.max(wallHi, coreHi);
                const bodyH = Math.max(1e-12, coreHi - coreLo);
                const minH = Math.max(bodyH * 2.2, mid * 0.0035);
                if (wallHi - wallLo < minH) {
                    const m = (wallLo + wallHi) / 2;
                    wallLo = m - minH / 2;
                    wallHi = m + minH / 2;
                }
                if (wallHi - wallLo > maxH) {
                    const m = (wallLo + wallHi) / 2;
                    wallLo = m - maxH / 2;
                    wallHi = m + maxH / 2;
                }
                lo = wallLo;
                hi = wallHi;
            }
            bidQty = 0;
            askQty = 0;
            levels = 0;
            for (const [px, qty] of bids) {
                if (px < lo || px > hi)
                    continue;
                bidQty += qty;
                levels += 1;
            }
            for (const [px, qty] of asks) {
                if (px < lo || px > hi)
                    continue;
                askQty += qty;
                levels += 1;
            }
        }
        const heightPct = mid > 0 ? ((hi - lo) / mid) * 100 : 0;
        const confirmQty = z.side === 'bull' ? bidQty : askQty;
        let strength = z.strength ?? 50;
        if (confirmQty > 0)
            strength = Math.min(100, strength + 6 + Math.min(8, levels));
        if (z.status === 'mitigated')
            strength = Math.min(strength, 40);
        return {
            ...z,
            priceLow: lo,
            priceHigh: hi,
            bookBidQty: bidQty || undefined,
            bookAskQty: askQty || undefined,
            bookLevels: levels || undefined,
            heightPct,
            strength,
        };
    });
};
